import type { AnimationFileLike, IdleMotionStrategy } from "./idleMotionStrategy";

/**
 * 受付（/reception）の待機中に再生するモーションファイル名のリスト。
 *
 * ★ここに受付で使う実際のモーションファイル名を列挙する★
 * - サーバ `getAnimationFiles()` が返す `name`（= 拡張子付きの完全なファイル名）と
 *   **完全一致**するものだけが再生対象になる。部分一致（includes）ではない点に注意。
 * - 拡張子（.vrma / .fbx）まで含めて正確に記述すること。
 * - 大文字小文字も区別する完全一致。S3 の実ファイル名と大文字小文字含め完全一致で記述すること
 *   （例: 実名が `Idle.fbx`〈大文字 I〉なら、ここも `Idle.fbx` と書く）。
 * - ここに列挙したファイル名**以外**のモーションは待機として再生しない（指定外モーションは使わない）。
 *
 * 実運用で使うモーションが決まったら、この配列を編集すること。
 */
export const RECEPTION_IDLE_MOTIONS: string[] = ["Idle.fbx", "Standing Idle.fbx"];

/**
 * 待機モーションの選択方式。
 * - "random": リストからランダムに 1 つ選ぶ（既定。現状の体感を維持）。
 * - "sequential": リストを先頭から順に巡回する（毎回次のモーションへ）。
 */
export type ReceptionIdleSelection = "random" | "sequential";
export const RECEPTION_IDLE_SELECTION = "random" as ReceptionIdleSelection;

/**
 * 受付専用の待機モーション選択ストラテジーを生成する。
 *
 * - `RECEPTION_IDLE_MOTIONS` と**完全一致**（大文字小文字も区別する）するファイルだけを
 *   再生対象に絞り込む（部分一致・フォールバックは行わない。指定外モーションは一切再生しない）。
 * - selection が "sequential" の場合はクロージャ内の index で巡回、
 *   "random" の場合はランダムに 1 つ返す。
 * - 一致するファイルが 0 件の場合は `null` を返す（Model 側で現在アニメをループ）。
 *   この際、ファイル名不一致を一度だけ `console.info` で通知する。
 *
 * 注意: "sequential" の index を保持するため、インスタンスは使い回すこと
 * （受付側では useMemo で 1 回だけ生成する）。
 */
export const createReceptionIdleMotionStrategy = (): IdleMotionStrategy => {
  // sequential 用の巡回インデックス（クロージャで保持）。
  let sequentialIndex = 0;
  // ファイル名不一致の警告を一度だけ出すためのフラグ。
  let hasWarnedNoMatch = false;

  const pickIdle = (files: AnimationFileLike[]): AnimationFileLike | null => {
    // RECEPTION_IDLE_MOTIONS と完全一致（大文字小文字も区別する）するものだけに絞り込む。
    const candidates = files.filter((file) => RECEPTION_IDLE_MOTIONS.includes(file.name));

    if (candidates.length === 0) {
      if (!hasWarnedNoMatch) {
        hasWarnedNoMatch = true;
        console.info(
          "[receptionIdleMotion] RECEPTION_IDLE_MOTIONS に指定したファイル名が animationFiles に見つかりません。" +
            " ファイル名（拡張子含む完全一致）を確認してください。",
          { expected: RECEPTION_IDLE_MOTIONS, available: files.map((f) => f.name) }
        );
      }
      return null;
    }

    if (RECEPTION_IDLE_SELECTION === "sequential") {
      const choice = candidates[sequentialIndex % candidates.length];
      sequentialIndex = (sequentialIndex + 1) % candidates.length;
      return choice;
    }

    // "random"
    return candidates[Math.floor(Math.random() * candidates.length)];
  };

  return { pickIdle };
};
